<section class="panel family-panel">
  <div class="panel-head">
    <h3 class="panel-title">Business family</h3>
    @if (family) {
      @if (requested?.structureRole === 'branch') {
        <span class="pill pill-purple">Branch</span>
      } @else if (isFamily) {
        <span class="pill">Head office</span>
      } @else {
        <span class="pill pill-grey">Standalone</span>
      }
    }
  </div>

  @if (loading) {
    <p class="muted">Loading the family…</p>
  } @else if (loadError) {
    <div class="note note-err">{{ loadError }}</div>
  } @else if (family) {
    <p class="status-line">{{ statusLine }}</p>

    @if (detachResult) {
      <div class="note note-ok detach-done">
        {{ detachedName }} is standalone now.
        @if (detachResult.catalogueCopy) {
          It received its own copy of {{ detachResult.catalogueCopy.items }} products,
          {{ detachResult.catalogueCopy.variants }} variants, {{ detachResult.catalogueCopy.categories }} categories
          and {{ detachResult.catalogueCopy.bundles }} bundles.
          @if (detachResult.catalogueCopy.skipped) {
            {{ detachResult.catalogueCopy.skipped }} could not be copied (deleted at head office).
          }
        }
        @if (detachResult.headOfficeBecameStandalone) { The head office has no branches left and is standalone again. }
      </div>
    }

    @if (isFamily) {
      <div class="family-grid">
        <div class="tree">
          <div class="tree-node hq" [class.current]="requested?.businessId === family.headOffice.businessId">
            <a [routerLink]="['/businesses', family.headOffice.businessId]">{{ family.headOffice.name }}</a>
            <span class="muted"> — head office</span>
          </div>
          @for (b of family.branches; track b.businessId; let last = $last) {
            <div class="tree-node branch" [class.current]="requested?.businessId === b.businessId">
              <span class="elbow" aria-hidden="true">{{ last ? '└─' : '├─' }}</span>
              <a [routerLink]="['/businesses', b.businessId]">{{ b.label || b.name }}</a>
              @if (b.code) { <span class="code">· {{ b.code }}</span> }
              <span class="muted">· {{ catalogueLabel(b) }}</span>
              @if (editing) {
                <button type="button" class="act-link danger detach-btn" (click)="openDetach(b)"
                  [disabled]="detaching" [attr.aria-label]="'Detach ' + (b.label || b.name)">Detach…</button>
              }
            </div>
          }

          <dl class="facts owner-facts">
            <div>
              <dt>Owner</dt>
              <dd>
                @if (family.owner) {
                  <a [routerLink]="['/users', family.owner.userId]">{{ family.owner.name || family.owner.email || family.owner.userId }}</a>
                } @else { <span class="warn">No owner</span> }
              </dd>
            </div>
            <div><dt>Plan</dt><dd>{{ family.planName || '—' }}</dd></div>
            <div>
              <dt>Businesses</dt>
              <dd>{{ family.accountBusinessCount }} ({{ family.familyCount }} in this family)</dd>
            </div>
          </dl>
        </div>

        <div class="history">
          <div class="section-caption">Structure history</div>
          @if (!history.length) {
            <p class="muted small">No structure changes recorded.</p>
          } @else {
            <ul class="events">
              @for (e of history; track e.eventId) {
                <li>
                  <span class="when">{{ e.occurredAtUtc | date: 'yyyy-MM-dd' }}</span>
                  · {{ describe(e) }}
                  @if (e.actorName) { · <span class="muted">{{ e.actorName }}</span> }
                  @if (e.reason) { <div class="reason muted">“{{ e.reason }}”</div> }
                </li>
              }
            </ul>
          }
        </div>
      </div>

      @if (!editing) {
        <p class="muted small hint">Switch to Edit to detach a branch.</p>
      }
    } @else if (history.length) {
      <div class="section-caption">Structure history</div>
      <ul class="events">
        @for (e of history; track e.eventId) {
          <li>
            <span class="when">{{ e.occurredAtUtc | date: 'yyyy-MM-dd' }}</span> · {{ describe(e) }}
            @if (e.actorName) { · <span class="muted">{{ e.actorName }}</span> }
            @if (e.reason) { <div class="reason muted">“{{ e.reason }}”</div> }
          </li>
        }
      </ul>
    }

    @if (detachTarget) {
      <!-- Static confirm: closed only by Cancel or a successful detach (no outside click). -->
      <div class="detach-form" role="dialog" aria-modal="false" [attr.aria-label]="'Detach ' + (detachTarget.label || detachTarget.name)">
        <h4>Detach {{ detachTarget.label || detachTarget.name }}?</h4>
        <p class="small">
          It becomes a standalone business.
          @if (detachTarget.catalogueMode !== 'own') {
            It first gets its own copy of every product it has stock of, has sold or has set branch prices for,
            at its branch prices; its past sales keep pointing at the head office's products.
          } @else {
            It already has its own catalogue, so nothing is copied.
          }
          The change is recorded with your name and reason.
        </p>
        <label class="field">
          <span>Reason (required)</span>
          <textarea class="inp" rows="2" maxlength="500" [(ngModel)]="detachReason"
            placeholder="e.g. Created by mistake — merchant asked for a separate business"></textarea>
        </label>
        @if (detachError) { <div class="note note-err">{{ detachError }}</div> }
        <div class="form-actions">
          <button type="button" class="act-ghost" (click)="cancelDetach()" [disabled]="detaching">Cancel</button>
          <button type="button" class="act-danger" (click)="confirmDetach()" [disabled]="!canConfirmDetach">
            {{ detaching ? 'Detaching…' : 'Detach branch' }}
          </button>
        </div>
      </div>
    }

    <p class="muted small footnote">Support can't link businesses — owners link their own, and only businesses with the same owner (B3).</p>
  }
</section>
